"use client";

import { Translate } from "@/components/Translate";
import { sizes } from "@/components/bloks/Asset";
import { Close24 } from "@/components/icons/Close24";
import { Minus16Icon } from "@/components/icons/Minus16";
import { Plus16Icon } from "@/components/icons/Plus16";
import { IconButton } from "@/components/ui/IconButton";
import { getProductName, formatPrice } from "@/lib/utils";
import { useRemoveFromCart, useSetQuantity } from "@frend-digital/centra/client";
import { clsx } from "@frend-digital/ui";
import Image from "next/image";
import { Link } from "@/i18n/Link";
import styles from "./cart.module.css";
import { type CartItem as CartItemType, useCart } from "./hooks";
import { useTranslations } from "@/i18n/client";
import { useCampaigns } from "@/features/campaigns/hooks";
import { useGlobal } from "@/features/global/GlobalContext";
import type { Campaign } from "@/features/campaigns/dal";

// Helper function to check if a product is in a campaign's products list
const isProductInCampaign = (
	campaign: Campaign | undefined,
	productId: string | undefined,
	productSku: string | undefined,
): boolean => {
	if (!campaign) return false;
	const productIdMatch =
		productId && typeof productId === "string" && campaign.products.includes(productId);
	const skuMatch =
		productSku && typeof productSku === "string" && campaign.products.includes(productSku);
	return !!(productIdMatch || skuMatch);
};

export const CartItems = ({ className = "" }) => {
	const { data } = useCart();
	const { data: campaigns } = useCampaigns();
	const { data: globalData } = useGlobal();

	// Count items per campaign that are actually in the campaign's products list. Needed to check if 3 for 2 or 2 for 1 discount is valid
	const campaignItemCounts = new Map<string, number>();

	data?.items?.forEach((item) => {
		if (item.campaign?.name) {
			const campaign = campaigns?.find((c) => c.name === item.campaign?.name);
			if (isProductInCampaign(campaign, item.product?.product, item.product?.productSku)) {
				const currentCount = campaignItemCounts.get(item.campaign.name) || 0;
				campaignItemCounts.set(item.campaign.name, currentCount + (item.quantity || 1));
			}
		}
	});

	return (
		<div className={clsx(styles.items, className)}>
			{data?.items?.map((item) => {
				const itemVoucherDiscount =
					item.discounts?.vouchers?.reduce(
						(acc, voucher) => acc + Math.abs(voucher.priceOffAsNumber || 0),
						0,
					) || 0;

				const voucherDiscountPercent =
					item?.totalPriceBeforeDiscountAsNumber! > 0 && itemVoucherDiscount > 0
						? Math.round((itemVoucherDiscount / item?.totalPriceBeforeDiscountAsNumber!) * 100)
						: 0;

				const hasVoucherDiscount = itemVoucherDiscount > 0;

				// Get campaign data
				const campaign = item.campaign?.name
					? campaigns?.find((c) => c.name === item.campaign?.name)
					: undefined;
				const campaignItemCount = item.campaign?.name
					? campaignItemCounts.get(item.campaign?.name) || 0
					: 0;
				const isItemInCampaign = isProductInCampaign(
					campaign,
					item.product?.product,
					item.product?.productSku,
				);
				const campaignTagConfig = campaign ? globalData.tags[campaign.campaign] : undefined;
				const colorEntry = campaignTagConfig?.color
					? globalData.colorMap[campaignTagConfig.color]
					: undefined;

				return (
					<CartItem
						key={item.item}
						item={item}
						voucherDiscountPercent={voucherDiscountPercent}
						hasVoucherDiscount={hasVoucherDiscount}
						campaignItemCount={campaignItemCount}
						isItemInCampaign={isItemInCampaign}
						campaignTagConfig={campaignTagConfig}
						colorEntry={colorEntry}
						currency={data?.currency}
					/>
				);
			})}
		</div>
	);
};

export const getCentraMedia = (media: Record<string, string[]> | undefined, key: string) => {
	if (!media) return undefined;

	return {
		href: media[key]?.[0],
		alt: "",
	};
};

export const CartItem = ({
	item,
	voucherDiscountPercent,
	hasVoucherDiscount,
	campaignItemCount,
	isItemInCampaign,
	campaignTagConfig,
	colorEntry,
	currency,
}: {
	item: CartItemType;
	voucherDiscountPercent: number;
	hasVoucherDiscount: boolean;
	campaignItemCount: number;
	isItemInCampaign: boolean;
	campaignTagConfig?: { name: string; color?: string | number };
	colorEntry?: { color: string; foreground: string | null | undefined };
	currency?: string;
}) => {
	const setQuantity = useSetQuantity({
		optimistic: true,
	});

	const { mutate } = useRemoveFromCart();

	const media = getCentraMedia(item.product?.media, "standard");
	const t = useTranslations();
	const id = `cart-item-title-${item.line}`;

	const hasDiscount =
		(item.totalPriceAfterDiscountAsNumber ?? 0) < (item.totalPriceBeforeDiscountAsNumber ?? 0);

	// Show campaign color only when all conditions are met
	const shouldShowCampaignColor =
		isItemInCampaign && colorEntry && hasDiscount && campaignItemCount > 0;

	return (
		<>
			<article className={styles.item} aria-labelledby={id}>
				<h2 className="sr-only">{getProductName(item.product)}</h2>
				<div className={styles.imageWrapper}>
					{item.totalPriceAfterDiscountAsNumber === 0 ? (
						<span className={styles.discountBadge}>
							<Translate>Free</Translate>
						</span>
					) : shouldShowCampaignColor ? (
						<span
							className={styles.discountBadge}
							style={
								{
									backgroundColor: colorEntry.color,
									color: colorEntry.foreground,
								} as React.CSSProperties
							}
						>
							<Translate>{campaignTagConfig?.name || ""}</Translate>
						</span>
					) : item.anyDiscount || hasVoucherDiscount ? (
						<span
							className={clsx(
								styles.discountBadge,
								item.anyDiscount && item.campaign?.name === "Outlet"
									? styles.discountBadgeRed
									: styles.discountBadgeYellow,
							)}
						>
							{item.anyDiscount
								? `-${item.product?.discountPercent}%`
								: hasVoucherDiscount
									? `-${voucherDiscountPercent}%`
									: null}
						</span>
					) : null}

					{media && (
						<div className={styles.image}>
							<Image sizes={sizes(50, 30)} src={media.href} alt={media.alt} fill />
						</div>
					)}
				</div>
				<div className={styles.info}>
					<dl className={styles.content}>
						<div className={styles.row}>
							<dt id={id} className={styles.name}>
								<Link href={`/${item.product?.categoryUri || "products"}/${item.product?.uri}`}>
									{getProductName(item.product)}
								</Link>
							</dt>
							<button
								type="button"
								aria-label={t("Remove item from cart")}
								onClick={() => mutate({ line: item.line!, optimistic: true })}
								className={styles.removeButton}
							>
								<Close24 />
							</button>
						</div>
						<dd className={styles.price}>
							{shouldShowCampaignColor ? (
								<>
									<span
										className={styles.discountPriceBadge}
										style={
											{
												"--tag-color": colorEntry.color,
												"--tag-foreground": colorEntry.foreground,
												backgroundColor: colorEntry.color,
												color: colorEntry.foreground,
											} as React.CSSProperties
										}
									>
										{formatPrice(item.totalPriceAfterDiscountAsNumber?.toFixed(0))} {currency}
									</span>
									<span className={styles.originalPrice}>
										{" "}
										{formatPrice(item.totalPriceBeforeDiscountAsNumber?.toFixed(0))} {currency}
									</span>
								</>
							) : (
								<span
									className={clsx(
										(item.anyDiscount || hasVoucherDiscount) && styles.discountPriceBadge,
										item.totalPriceAfterDiscountAsNumber === 0
											? null
											: item.anyDiscount && item.campaign?.name === "Outlet"
												? styles.discountBadgeRed
												: item.anyDiscount || hasVoucherDiscount
													? styles.discountBadgeYellow
													: null,
									)}
								>
									{formatPrice(item.totalPriceAfterDiscountAsNumber?.toFixed(0))} {currency}
								</span>
							)}
							{(item.anyDiscount || hasVoucherDiscount) && !shouldShowCampaignColor && (
								<span className={styles.originalPrice}>
									{" "}
									{formatPrice(item.totalPriceBeforeDiscountAsNumber?.toFixed(0))} {currency}
								</span>
							)}
						</dd>
						<dd className={styles.size}>
							<Translate>Size:</Translate> {item.size}
						</dd>
						<dd className={styles.variant}>
							<Translate>Color:</Translate> {item.product?.variantName}
						</dd>

						<dd className={styles.sku}>
							<small>SKU: {item.product?.productSku}</small>
						</dd>
					</dl>
				</div>
				<menu
					className={styles.actions}
					// biome-ignore lint/a11y/useSemanticElements: <explanation>
					role="group"
					aria-label={`${t("Item quantity for")} ${getProductName(item.product)}`}
				>
					<IconButton
						className={styles.navButtons}
						onClick={() =>
							setQuantity.mutate({
								line: item.line!,
								quantity: (item.quantity ?? 0) - 1,
							})
						}
						aria-label={t("Decrease quantity")}
					>
						<Minus16Icon />
					</IconButton>
					<span aria-live="polite" aria-atomic className={styles.quantity}>
						{item.quantity}{" "}
						<div className="sr-only">
							<Translate>item(s)</Translate>
						</div>
					</span>
					<IconButton
						className={styles.navButtons}
						onClick={() =>
							setQuantity.mutate({
								line: item.line!,
								quantity: (item.quantity ?? 0) + 1,
							})
						}
						aria-label={t("Increase quantity")}
					>
						<Plus16Icon />
					</IconButton>
				</menu>
			</article>
		</>
	);
};
